import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import routeService from "services/routeService";

// List Route
export const useListRoute = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["route"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["routes", payload?.key || "", params?.page, params?.columnFilter, params?.keywords],
    () => routeService.listRoute(payload),
    {
      enabled: params?.run || false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("route"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
        };
      },
    }
  );
};

// Create Route
export const useCreateRoute = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["route"]);
  const { addToast } = useToasts();

  return useMutation(routeService.createRoute, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("route"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("route"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Detail Route
export const useDetailRoute = (payload, onSuccess, onError) => {
  const { t, i18n } = useTranslation(["route"]);
  const { addToast } = useToasts();

  return useQuery(
    ["route", "detail"],
    () => routeService.detailRoute(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.detail", {
                target: t("route"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// Update Route
export const useUpdateRoute = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["route"]);
  const { addToast } = useToasts();

  return useMutation(routeService.updateRoute, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("route"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("route"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Active Route
export const useActiveRoute = (onSuccess, onError) => {
  const { t } = useTranslation(["route"]);
  const { addToast } = useToasts();

  return useMutation(routeService.activeRoute, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;

      addToast(
        t("message.active", {
          target: t("route"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.active", {
              target: t("route"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Delete Route
export const useDeleteRoute = (onSuccess, onError) => {
  const { t } = useTranslation(["route"]);
  const { addToast } = useToasts();

  return useMutation(routeService.deleteRoute, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;

      addToast(
        t("message.delete", {
          target: t("route"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("route"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useClearAllRoute = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["route"]);
  const { addToast } = useToasts();

  return useMutation(routeService.clearAllRoute, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.clear", {
          target: "phân bổ tuyến vận chuyển",
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.clear", {
              target: "phân bổ tuyến vận chuyển",
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useClearRouteOfHub = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["route"]);
  const { addToast } = useToasts();

  return useMutation(routeService.clearRouteOfHub, {
    retry: 0,
    onSuccess: res => {
      if (res?.data?.result) {
        addToast(
          t("message.clear", {
            target: "phân bổ tuyến vận chuyển",
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
        if (typeof onSuccess === "function") {
          onSuccess(res);
        }
      } else {
        addToast(
          formConvertErrorMessage(
            res?.data?.message ||
              t("message.clear", {
                target: "phân bổ tuyến vận chuyển",
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(res);
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.clear", {
              target: "phân bổ tuyến vận chuyển",
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};
